import { MainContainer } from '@/components/MainContainer';
import { useTheme } from '@palmetto/palmetto-components';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import './DsmPageContainer.css';

interface DsmPageContainerProps {
  title: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}

const getSystemMode = (): 'light' | 'dark' =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

/**
 * Page-level container for DSM pages inside the legacy App shell.
 *
 * Handles:
 * - Full-bleed canvas-default background (counteracts parent padding/margin)
 * - data-theme="core" scope for DSM token resolution
 * - Helmet <title>
 * - Body background via data-theme/data-mode attributes so var(--canvas-default) resolves on <body>
 * - MainContainer for centered max-width layout
 *
 * Usage:
 *   <DsmPageContainer title="My Page">
 *     <PageHeader title="My Page" />
 *     {content}
 *   </DsmPageContainer>
 */
export const DsmPageContainer = ({ title, children, fullWidth = false }: DsmPageContainerProps) => {
  const { theme } = useTheme();
  const [systemMode, setSystemMode] = useState<'light' | 'dark'>(getSystemMode);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const resolvedMode = theme === 'system' ? systemMode : (theme as 'light' | 'dark');

  useEffect(() => {
    // DSM token resolution requires [data-mode] on an ANCESTOR of [data-theme=core].
    // The CSS rule is: [data-mode=dark] [data-theme=core] { --canvas-default: ... }
    // So we split the attributes: data-mode on <html>, data-theme on <body>.
    // This makes body[data-theme=core] a descendant of html[data-mode], triggering the rule.
    const prevHtmlMode = document.documentElement.getAttribute('data-mode');
    const prevBodyTheme = document.body.getAttribute('data-theme');
    document.documentElement.setAttribute('data-mode', resolvedMode);
    document.body.setAttribute('data-theme', 'core');
    return () => {
      if (prevHtmlMode) document.documentElement.setAttribute('data-mode', prevHtmlMode);
      else document.documentElement.removeAttribute('data-mode');
      if (prevBodyTheme) document.body.setAttribute('data-theme', prevBodyTheme);
      else document.body.removeAttribute('data-theme');
    };
  }, [resolvedMode]);

  return (
    <div className="dsm-page-container" data-theme="core">
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <MainContainer maxWidth={fullWidth ? '100' : undefined}>{children}</MainContainer>
    </div>
  );
};
